<template>
  <div id="app" class="d-flex flex-column min-vh-100">
    <UpdateNotificationBanner :registration="swRegistration" :version-mismatch="versionMismatch" />
    <Sidenav
      :custom_class="color"
      :class="[isRTL ? 'fixed-end' : 'fixed-start']"
      v-if="showSidenav"
    />
    <main
      class="main-content position-relative max-height-vh-100 h-100 overflow-x-hidden"
      :class="innerWidth > 1200 ? 'px-5' : 'px-3'"
    >
      <!-- nav -->
      <Navbar
        :class="[isNavFixed ? navbarFixed : '', isAbsolute ? absolute : '']"
        :color="isAbsolute ? 'text-white opacity-8' : ''"
        :minNav="navbarMinimize"
        v-if="showNavbar"
      />
      <PromoBannerCarousel v-if="showNavbar" />
      <router-view />
      <Toast />
      <AppFooter v-show="showFooter" />
      <FloatSocialDock />
      <LegalAcceptanceModal
        :isOpen="showLegalModal"
        :pending="legalPending"
        @accepted="onLegalAccepted"
      />
      <TwoFAAnnouncementModal 
        :isOpen="show2FAModal" 
        @confirm="confirm2FAModal"
        @settings="goTo2FASettings"
      />
      <FirstCouponModal
        :isOpen="showFirstCouponModal"
        :code="firstCouponCode"
        :percent="firstCouponPercent"
        @confirm="confirmFirstCouponModal"
        @create="goCreateWithCoupon"
      />
      <PlanExpiredModal :isOpen="showPlanExpiredModal" />
      <PlanRenewSoonModal
        :isOpen="showPlanRenewSoonModal"
        :days-left="planDaysLeft"
        :expiration-label="planExpirationLabel"
        @close="closePlanRenewSoon"
      />
      <AffiliateWithdrawPromptModal
        :isOpen="showAffiliateWithdrawPrompt"
        :balance="affiliateWalletBalance"
        @close="showAffiliateWithdrawPrompt = false"
      />
    </main>
  </div>
</template>
<script>
import Sidenav from "./examples/Sidenav";
import Navbar from "@/examples/Navbars/Navbar.vue";
import AppFooter from "@/examples/Footer.vue";
import { mapMutations, mapState } from "vuex";
import Toast from "primevue/toast";
import FloatSocialDock from "./components/FloatSocialDock.vue";
import PromoBannerCarousel from "./components/PromoBannerCarousel.vue";
import UpdateNotificationBanner from "./components/UpdateNotificationBanner.vue";
import TwoFAAnnouncementModal from "./components/TwoFAAnnouncementModal.vue";
import FirstCouponModal from "./components/FirstCouponModal.vue";
import PlanExpiredModal from "./components/Modals/PlanExpiredModal.vue";
import PlanRenewSoonModal from "./components/Modals/PlanRenewSoonModal.vue";
import AffiliateWithdrawPromptModal from "./components/AffiliateWithdrawPromptModal.vue";
import LegalAcceptanceModal from "./components/LegalAcceptanceModal.vue";
import UsersHttp from "@/http/UsersHttp.js";
import AffiliateHttp from "@/http/AffiliateHttp.js";
import LegalHttp from "@/http/LegalHttp.js";
import { capturePlatformAffiliateRef } from "@/utils/platformAffiliateRef.js";
import { applyThemeClasses } from "@/utils/themePreference.js";

import AuthHttp from "@/http/AuthHttp.js";
export default {
  name: "App",
  components: {
    Sidenav,
    Navbar,
    AppFooter,
    Toast,
    FloatSocialDock,
    PromoBannerCarousel,
    UpdateNotificationBanner,
    LegalAcceptanceModal,
    TwoFAAnnouncementModal,
    FirstCouponModal,
    PlanExpiredModal,
    PlanRenewSoonModal,
    AffiliateWithdrawPromptModal,
  },
  data() {
    return {
      innerWidth: window.innerWidth,
      swRegistration: null,
      versionMismatch: false,
      showLegalModal: false,
      legalPending: [],
      show2FAModal: false,
      showFirstCouponModal: false,
      firstCouponCode: "RIFA50",
      firstCouponPercent: 50,
      showPlanRenewSoonModal: false,
      planDaysLeft: 0,
      planExpirationLabel: "",
      showAffiliateWithdrawPrompt: false,
      affiliateWalletBalance: 0,
    };
  },
  methods: {
    ...mapMutations(["toggleConfigurator", "navbarMinimize"]),

    // Verifica atualizações periodicamente (chamado pelo polling)
    async checkForUpdates() {
      // Não executa em ambiente de desenvolvimento
      const isDev = process.env.NODE_ENV !== 'production' ||
        ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (isDev) return;

      // Estratégia 1: força o SW a verificar se o service-worker.js mudou no servidor
      if (window.__swRegistration) {
        try {
          await window.__swRegistration.update();
          console.log('[App] SW verificado pelo polling.');
        } catch (e) {
          console.warn('[App] Falha ao verificar SW:', e);
        }
      }

      // Estratégia 2: faz fetch de /version.json para detectar mudança de versão via HTTP
      // (funciona mesmo sem SW, ideal para usuários com PWA não instalado)
      try {
        const res = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          const storedVersion = this.$store.getters.app_version;
          if (storedVersion && data.version && data.version !== storedVersion) {
            console.log(`[App] Nova versão detectada via polling: ${storedVersion} → ${data.version}`);
            this.$store.commit('setAppVersion', data.version);
            this.versionMismatch = true;
          }
        }
      } catch (e) {
        // Silently ignore — /version.json pode não existir
      }
    },

    async verifyAuth() {
      if (
        this.$route.path == "/sign-up" ||
        this.$route.name == "Validar" ||
        this.$route.name == "NovaSenha"
      ) {
        return;
      }
      const token = this.$store.getters.token;

      if (token) {
        try {
          const response = await AuthHttp.validateSession();

          if (response.data.status == 401) {
            this.$router.push("/sign-in");
          }
        } catch (error) {
          this.$router.push("/sign-in");
          console.error(error);
        }
      } else {
        this.$router.push("/sign-in");
      }
    },
    isPublicAuthRoute() {
      const path = this.$route?.path || "";
      const name = this.$route?.name || "";
      return (
        path.startsWith("/sign-in") ||
        path.startsWith("/sign-up") ||
        path.startsWith("/validar") ||
        path.startsWith("/nova-senha") ||
        name === "Validar" ||
        name === "NovaSenha"
      );
    },
    check2FAModal() {
      // Nunca mostra popups na tela de login/cadastro
      if (this.isPublicAuthRoute()) {
        this.showLegalModal = false;
        this.show2FAModal = false;
        this.showFirstCouponModal = false;
        return;
      }

      const token = this.$store.getters.token;
      const user = this.$store.getters.user;

      if (!token || !user?.id) {
        this.showLegalModal = false;
        this.show2FAModal = false;
        this.showFirstCouponModal = false;
        return;
      }

      // Aceite legal primeiro (bloqueante)
      this.checkLegalAcceptance();
    },
    async checkLegalAcceptance() {
      try {
        if (this.isPublicAuthRoute()) {
          this.showLegalModal = false;
          return;
        }
        const token = this.$store.getters.token;
        const user = this.$store.getters.user;
        if (!token || !user?.id || user.role === "ADMIN") {
          this.showLegalModal = false;
          this.continueAfterLegal();
          return;
        }
        const { data } = await LegalHttp.getStatus();
        if (data?.must_accept && Array.isArray(data.pending) && data.pending.length) {
          this.legalPending = data.pending;
          this.showLegalModal = true;
          this.show2FAModal = false;
          this.showFirstCouponModal = false;
          return;
        }
        this.showLegalModal = false;
        this.continueAfterLegal();
      } catch (e) {
        console.warn("[legal] status check fail-open", e);
        this.showLegalModal = false;
        this.continueAfterLegal();
      }
    },
    continueAfterLegal() {
      const user = this.$store.getters.user;
      if (user?.seen_2fa_modal === false) {
        this.show2FAModal = true;
      } else {
        this.checkFirstCouponOffer();
      }
    },
    onLegalAccepted() {
      this.showLegalModal = false;
      this.legalPending = [];
      this.continueAfterLegal();
    },
    async confirm2FAModal() {
      try {
        await UsersHttp.mark2FaModalSeen();
        const user = this.$store.getters.user;
        user.seen_2fa_modal = true;
        this.$store.commit("setUser", user);
      } catch (e) {
        console.error("Erro ao marcar modal como visto:", e);
      } finally {
        this.show2FAModal = false;
        if (!this.isPublicAuthRoute()) {
          this.checkFirstCouponOffer();
        }
      }
    },
    async goTo2FASettings() {
      await this.confirm2FAModal();
      this.$router.push({ name: "Configurações", query: { tab: "security" } });
    },
    async checkFirstCouponOffer() {
      try {
        if (this.isPublicAuthRoute()) {
          this.showFirstCouponModal = false;
          return;
        }
        const token = this.$store.getters.token;
        const user = this.$store.getters.user;
        if (!token || !user?.id || user.role === "ADMIN") return;
        if (this.show2FAModal) return;
        if (sessionStorage.getItem(`seen_first_coupon_${user.id}`) === "1") return;

        const { data } = await UsersHttp.getFirstCouponOffer();
        if (data?.show && !this.isPublicAuthRoute() && this.$store.getters.token) {
          this.firstCouponCode = data.code || "RIFA50";
          this.firstCouponPercent = data.percent || 50;
          this.showFirstCouponModal = true;
        }
      } catch (e) {
        console.warn("[first-coupon] offer check fail-open", e);
      }
    },
    async confirmFirstCouponModal() {
      const user = this.$store.getters.user;
      if (user?.id) {
        sessionStorage.setItem(`seen_first_coupon_${user.id}`, "1");
      }
      this.showFirstCouponModal = false;
      try {
        await UsersHttp.markFirstCouponModalSeen();
      } catch (e) {
        console.error(e);
      }
    },
    async goCreateWithCoupon() {
      await this.confirmFirstCouponModal();
      try {
        await navigator.clipboard.writeText(this.firstCouponCode);
      } catch (_) {
        /* ignore */
      }
      this.$router.push({ name: "Nova campanha" });
    },
    checkPlanExpiration() {
      const user = this.$store.getters.user;
      // Admins são sempre liberados de qualquer bloqueio
      if (user && user.role === 'ADMIN') return;

      const isPlanArea = ["/plans", "/plans/pagamento"].includes(this.$route.path);

      // Prioridade 1: Flag forçada pela API/Login
      if (user && user.force_plan_page) {
        if (!isPlanArea) {
          this.$router.push("/plans");
          
          // Mostra o modal apenas se ainda não viu nesta sessão
          if (!sessionStorage.getItem('seen_plan_expired_modal')) {
            this.$store.commit("setPlanExpiredModal", true);
            sessionStorage.setItem('seen_plan_expired_modal', 'true');
          }
        }
        return;
      }

      // Prioridade 2: Verificação local (fallback para redundância)
      if (user && user.use_plan && (user.plan_active === "MENSAL" || user.plan_active === "ANUAL") && user.plan_expiration) {
        const raw = String(user.plan_expiration).split("T")[0];
        const [year, month, day] = raw.split("-").map(Number);
        if (!year || !month || !day) return;

        const expDate = new Date(year, month - 1, day, 23, 59, 59);
        const now = new Date();
        
        if (expDate < now) {
          if (!isPlanArea) {
            this.$router.push("/plans");
            
            if (!sessionStorage.getItem('seen_plan_expired_modal')) {
              this.$store.commit("setPlanExpiredModal", true);
              sessionStorage.setItem('seen_plan_expired_modal', 'true');
            }
          }
          return;
        }

        // Aviso de renovação: 7 dias antes do vencimento
        const msLeft = expDate.getTime() - now.getTime();
        const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
        if (daysLeft > 0 && daysLeft <= 7) {
          const seenKey = `seen_plan_renew_soon_${raw}`;
          if (!sessionStorage.getItem(seenKey)) {
            this.planDaysLeft = daysLeft;
            this.planExpirationLabel = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
            this.showPlanRenewSoonModal = true;
            sessionStorage.setItem(seenKey, "true");
          }
        }
      }
    },
    closePlanRenewSoon() {
      this.showPlanRenewSoonModal = false;
    },
    async checkAffiliateWithdrawPrompt() {
      const user = this.$store.getters.user;
      if (!user || !user.is_affiliate) return;
      if (this.$route.path === "/meus-indicados" || this.$route.path === "/sign-in") return;
      try {
        if (sessionStorage.getItem("seen_affiliate_withdraw_prompt")) return;
      } catch (e) {
        /* continue */
      }

      try {
        const balRes = await AffiliateHttp.getAffiliateWallet();
        const balance = Number(balRes.data?.balance ?? 0);
        if (balance < 100) return;
        if (balRes.data?.has_pending) return;

        this.affiliateWalletBalance = balance;
        this.showAffiliateWithdrawPrompt = true;
      } catch (e) {
        /* ignore */
      }
    },
  },
  computed: {
    ...mapState([
      "isRTL",
      "color",
      "isAbsolute",
      "isNavFixed",
      "navbarFixed",
      "absolute",
      "showSidenav",
      "showNavbar",
      "showFooter",
      "showConfig",
      "hideConfigButton",
      "showPlanExpiredModal",
    ]),
  },
  watch: {
    $route() {
      // Verifica atualizações a cada mudança de rota (apenas se logado e em produção)
      const isDev = process.env.NODE_ENV !== 'production' ||
        ['localhost', '127.0.0.1'].includes(window.location.hostname);
      if (!isDev && this.$store.getters.token) {
        this.checkForUpdates();
      }
      if (this.$route.query?.ref) {
        capturePlatformAffiliateRef(this.$route.query.ref);
      }
      this.check2FAModal();
      this.checkPlanExpiration();
      this.checkAffiliateWithdrawPrompt();
    },
  },
  mounted() {
    if (this.$route.query?.ref) {
      capturePlatformAffiliateRef(this.$route.query.ref);
    }
    // Não executa nada do updater em ambiente de desenvolvimento
    const isDev = process.env.NODE_ENV !== 'production' ||
      ['localhost', '127.0.0.1'].includes(window.location.hostname);
    if (isDev) {
      this.checkAffiliateWithdrawPrompt();
      return;
    }

    // 1. Verifica se a versão mudou (funciona independente do SW)
    const currentVersion = require('../package.json').version;
    const storedVersion = this.$store.getters.app_version;

    if (!storedVersion) {
      this.$store.commit('setAppVersion', currentVersion);
    } else if (storedVersion !== currentVersion) {
      console.log(`[App] Versão atualizada: ${storedVersion} → ${currentVersion}`);
      this.$store.commit('setAppVersion', currentVersion);
      this.versionMismatch = true;
    }

    // 2. Escuta o evento do Service Worker
    this._swUpdateHandler = (event) => {
      this.swRegistration = event.detail;
    };
    document.addEventListener('swUpdated', this._swUpdateHandler);

    // 3. Polling periódico a cada 5 minutos
    this._updatePollingInterval = setInterval(() => {
      this.checkForUpdates();
    }, 5 * 60 * 1000);

    this.check2FAModal();
    this.checkPlanExpiration();
    this.checkAffiliateWithdrawPrompt();
  },
  beforeUnmount() {
    document.removeEventListener('swUpdated', this._swUpdateHandler);
    clearInterval(this._updatePollingInterval);
  },
  beforeMount() {
    this.$store.state.isTransparent = "bg-transparent";
    applyThemeClasses(!!this.$store.state.isDarkMode);

    const sidenav = document.getElementsByClassName("g-sidenav-show")[0];

    if (window.innerWidth > 1200 && sidenav) {
      sidenav.classList.add("g-sidenav-pinned");
    }
  },
};
</script>

<style>
/* Global Modal Enhancements */
.modal-backdrop {
  z-index: 9990 !important;
}
.modal-backdrop.show {
  opacity: 1 !important;
  background-color: rgba(15, 15, 20, 0.4) !important;
  backdrop-filter: blur(12px) !important;
  -webkit-backdrop-filter: blur(12px) !important;
}
.modal {
  z-index: 9999 !important;
}

/* Fix unexpected body layout shifts when modal is open */
body.modal-open {
  overflow: hidden !important;
  padding-right: 0 !important;
}

/* Prevent internal scrolling and hide scrollbar during modal */
body.modal-open .main-content {
  overflow: hidden !important;
}

/* Push the Sidenav back behind the modal backdrop */
body.modal-open .sidenav {
  z-index: 9900 !important;
}
</style>

<style scoped>
#app {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.main-content {
  flex-grow: 1;
}
</style>